import { useRef, useState } from 'react';
import { C, SERIF, mono } from '../../theme';
import { Icon } from '../../icons';
import { Loading, Empty } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, ApiError } from '../../api';
import type { DocumentRecord, TenancyRow } from '../../api';
import { useAsync } from '../../useAsync';
import { fmtDate } from '../../format';
import { cap } from '../helpers';

const DOC_KINDS = ['lease', 'id', 'gst', 'noc', 'insurance', 'receipt', 'invoice', 'other'] as const;

interface DocsData {
  docs: DocumentRecord[];
  tenancies: TenancyRow[];
}

function fmtSize(bytes?: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Documents vault — every file under the property (leases, IDs, GST, receipts…),
 * newest first, each opening its bytes in a new tab. The Upload panel posts a
 * multipart file scoped to the property or a specific tenancy.
 */
export function DocumentsPage({ propertyId, propertyName }: { propertyId?: string; propertyName?: string }) {
  const notify = useToast();
  const { data, loading, error, reload } = useAsync<DocsData>(async () => {
    const [docs, tenancies] = await Promise.all([
      api.documents(),
      api.tenancies().catch(() => [] as TenancyRow[]),
    ]);
    return { docs, tenancies };
  }, [propertyId]);

  const [showUpload, setShowUpload] = useState(false);

  const docs = [...(data?.docs ?? [])].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  const tenancies = data?.tenancies ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: SERIF, fontSize: 22, margin: '0 0 3px', fontWeight: 600 }}>Documents</h2>
          <div style={{ color: C.muted, fontSize: 13.5 }}>
            {propertyName ?? 'Property'}
            {data ? ` · ${docs.length} on file` : ''}
          </div>
        </div>
        <button onClick={() => setShowUpload((s) => !s)} style={primaryBtn}>
          <Icon n={showUpload ? 'chev' : 'doc'} size={15} color="#fff" style={showUpload ? { transform: 'rotate(-90deg)' } : undefined} />
          {showUpload ? 'Close' : 'Upload'}
        </button>
      </div>

      {showUpload && (
        <UploadPanel
          propertyId={propertyId}
          tenancies={tenancies}
          onDone={() => {
            setShowUpload(false);
            reload();
          }}
          notify={notify}
        />
      )}

      {loading ? (
        <Loading label="Loading documents…" minHeight={340} />
      ) : error ? (
        <Empty icon="doc" title="Documents unavailable" hint="We couldn't reach the document vault." minHeight={340}>
          <button onClick={reload} style={retryBtn}>Retry</button>
        </Empty>
      ) : docs.length === 0 ? (
        <Empty icon="doc" title="No documents yet" hint="Upload a lease, ID, GST certificate or receipt to keep it safe here." minHeight={300}>
          <button onClick={() => setShowUpload(true)} style={retryBtn}>Upload a document</button>
        </Empty>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflow: 'hidden' }}>
          {docs.map((d, i) => {
            const meta = [cap(d.kind), fmtSize(d.size), d.createdAt ? fmtDate(d.createdAt) : ''].filter(Boolean).join(' · ');
            return (
              <div
                key={d.id}
                onClick={() => window.open(api.documentContentUrl(d.id), '_blank', 'noopener')}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderTop: i ? `1px solid ${C.line2}` : 0, cursor: 'pointer' }}
              >
                <span style={{ width: 38, height: 38, borderRadius: 10, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                  <Icon n="doc" size={17} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ color: C.ink, fontWeight: 600, fontSize: 14, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</b>
                  <small style={{ color: C.muted, fontSize: 12 }}>{meta}</small>
                </div>
                {d.expiryDate && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: d.expiryDate < Date.now() ? C.red : '#9a6a12', background: d.expiryDate < Date.now() ? '#fbe7e3' : '#fbf0dd', borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>
                    {d.expiryDate < Date.now() ? 'Expired' : `Expires ${fmtDate(d.expiryDate)}`}
                  </span>
                )}
                {d.uploadedByRole === 'tenant' && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.accent2, background: C.soft, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>By tenant</span>
                )}
                {d.sharedWithTenant && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.hi, background: C.greenSoft, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>Shared</span>
                )}
                <Icon n="chev" size={15} color={C.muted} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UploadPanel({
  propertyId,
  tenancies,
  onDone,
  notify,
}: {
  propertyId?: string;
  tenancies: TenancyRow[];
  onDone: () => void;
  notify: (s: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<(typeof DOC_KINDS)[number]>('lease');
  const [tenancyId, setTenancyId] = useState('');
  const [shared, setShared] = useState(false);
  const [expiry, setExpiry] = useState(''); // yyyy-mm-dd
  const [busy, setBusy] = useState(false);

  const scopeOk = !!tenancyId || !!propertyId;
  const canSubmit = !!file && scopeOk && !busy;

  async function submit() {
    if (!file) return;
    if (!scopeOk) {
      notify('Pick a tenancy to file this against.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      fd.append('title', title.trim() || file.name);
      if (tenancyId) fd.append('tenancyId', tenancyId);
      else if (propertyId) fd.append('propertyId', propertyId);
      if (shared && tenancyId) fd.append('shared_with_tenant', 'true');
      if (expiry) {
        const ms = new Date(`${expiry}T00:00:00`).getTime();
        if (Number.isFinite(ms)) fd.append('expiry_date', String(ms));
      }
      await api.uploadDocument(fd);
      notify(kind === 'insurance' && expiry ? 'Insurance filed. Renewal reminder set.' : 'Document uploaded');
      onDone();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* file picker */}
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', border: `1.5px dashed ${C.line}`, borderRadius: 10, background: C.bg, cursor: 'pointer', textAlign: 'left', color: file ? C.ink : C.muted, fontSize: 13.5 }}
      >
        <Icon n="doc" size={17} color={C.accent2} />
        <span style={{ flex: 1, ...mono({ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>{file ? file.name : 'Choose a file…'}</span>
        {file && <span style={{ color: C.muted, fontSize: 12 }}>{fmtSize(file.size)}</span>}
      </button>
      <input
        ref={fileRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setFile(f);
          if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''));
        }}
      />

      <label style={fieldLabel}>Title
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Sequeira Motors lease deed" style={input} />
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={fieldLabel}>Kind
          <select value={kind} onChange={(e) => setKind(e.target.value as (typeof DOC_KINDS)[number])} style={input}>
            {DOC_KINDS.map((k) => (<option key={k} value={k}>{cap(k)}</option>))}
          </select>
        </label>
        <label style={fieldLabel}>Filed against
          <select value={tenancyId} onChange={(e) => setTenancyId(e.target.value)} style={input}>
            <option value="">Property (all)</option>
            {tenancies.map((t) => (<option key={t.id} value={t.id}>{t.unitLabel} · {t.tenantName}</option>))}
          </select>
        </label>
      </div>

      <label style={fieldLabel}>{kind === 'insurance' ? 'Policy expiry (sets a renewal reminder)' : 'Expiry date (optional)'}
        <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} style={input} />
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: tenancyId ? C.ink2 : C.muted, cursor: tenancyId ? 'pointer' : 'default' }}>
        <input type="checkbox" checked={shared && !!tenancyId} disabled={!tenancyId} onChange={(e) => setShared(e.target.checked)} />
        Share with the tenant {tenancyId ? '' : '(select a tenancy first)'}
      </label>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={submit} disabled={!canSubmit} style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? 'pointer' : 'default' }}>
          {busy ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  background: C.accent,
  color: '#fff',
  border: 0,
  borderRadius: 9,
  padding: '9px 14px',
  fontWeight: 600,
  fontSize: 13.3,
  cursor: 'pointer',
};

const fieldLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  fontSize: 12,
  fontWeight: 600,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
};

const input: React.CSSProperties = {
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  padding: '9px 11px',
  fontSize: 13.5,
  color: C.ink,
  background: '#fff',
  fontFamily: 'inherit',
  textTransform: 'none',
  letterSpacing: 'normal',
  fontWeight: 400,
};

const retryBtn: React.CSSProperties = {
  marginTop: 4,
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};
